import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import axios, { AxiosError } from "axios";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import SearchBox from "components/common/SearchBox";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import { ChangeEvent } from "react";
import { Spinner, Card } from "react-bootstrap";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import PrincipioAtivoTable, {
  principioAtivoTableColumns,
} from "components/tables/PrincipioAtivoTable";
import {
  PrincipioAtivoTableType,
  principiosAtivosBreadcrumbItems,
} from "data/principiosAtivos";
import AccessDeniedOverlay from "components/modules/common/AccessDeniedOverlay";
import { usePermissionModal } from "contexts/PermissionModalContext";

const fetchPrincipiosAtivos = async () => {
  const { data } = await axios.get("/api/principioAtivo");
  return data;
};

const PrincipiosAtivos = () => {
  const { getQueryRetryHandler, showPermissionModal } = usePermissionModal();

  const dataQuery = useQuery<PrincipioAtivoTableType[]>({
    queryKey: ["principiosAtivos"],
    queryFn: fetchPrincipiosAtivos,
    placeholderData: keepPreviousData,
    retry: getQueryRetryHandler({
      queryKey: ["principiosAtivos"],
      defaultMessage:
        "Você não tem permissão para consultar ou visualizar princípios ativos.",
    }),
  });

  const table = useReactTable({
    data: dataQuery.data ?? [],
    columns: principioAtivoTableColumns,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  if (dataQuery.isLoading)
    return (
      <Spinner animation="border" role="status" className="d-block mx-auto">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );

  if (dataQuery.error) {
    const error = dataQuery.error as AxiosError;
    if (error.response?.status === 403) {
      showPermissionModal("Você não tem permissão para consultar ou visualizar princípios ativos.");
      return null;
    }

    return (
      <div className="text-center py-5 text-danger">
        {(dataQuery.error as Error).message}
      </div>
    );
  }

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  return (
    <div>
      <PageBreadcrumb items={principiosAtivosBreadcrumbItems} />
      <div className="mb-9">
        <h2 className="mb-5">Princípios Ativos</h2>

        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex flex-wrap gap-3">
              <SearchBox
                placeholder="Pesquisar princípio ativo"
                onChange={handleSearchInputChange}
              />
              <div className="ms-xxl-auto">
                <Button
                  variant="primary"
                  as={Link}
                  to="/cadastros/principios-ativos/novo"
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Cadastrar
                </Button>
              </div>
            </div>
          </div>

          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
            <PrincipioAtivoTable />
          </div>
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default PrincipiosAtivos;
